import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import StatsCard from "../../components/StatsCard";
import PipelineSummary from "../../components/PipelineSummary";
import { Card } from "../../components/ui/card";
import { FileText, CheckCircle2, MousePointerClick, BadgeCheck } from "lucide-react";
import { fmtDateTime } from "../../lib/constants";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";

export default function SponsorOverview() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const { t } = useT();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sponsor/dashboard");
        setData(data);
      } catch (e) {
        toast.error(errorMessage(e));
      }
    })();
  }, []);

  if (!data) return <div className="text-sm text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t("sponsor.overview.title")}</h1>
        <p className="mt-1 text-sm text-slate-400">{t("sponsor.overview.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard label={t("sponsor.stats.totalApps")} value={data.stats.total_applications} icon={FileText} accent="emerald" testId="stat-total-apps" />
        <StatsCard label={t("sponsor.stats.okToApply")} value={data.stats.ok_to_apply} icon={BadgeCheck} accent="emerald" testId="stat-ok-apply" />
        <StatsCard label={t("sponsor.stats.approved")} value={data.stats.approved} icon={CheckCircle2} accent="emerald" testId="stat-approved" />
        <StatsCard label={t("sponsor.stats.referralClicks")} value={data.stats.referral_clicks} icon={MousePointerClick} accent="amber" testId="stat-referral-clicks" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PipelineSummary pipeline={data.pipeline} />
        </div>
        <Card className="border-slate-800 bg-slate-900 p-5" data-testid="recent-notifications-card">
          <div className="mb-4 flex items-baseline justify-between">
            <div className="text-sm font-semibold">{t("sponsor.notifications.title")}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{t("sponsor.notifications.click")}</div>
          </div>
          <div className="space-y-2">
            {data.notifications.length === 0 && (
              <div className="rounded-md border border-dashed border-slate-800 p-4 text-center text-xs text-slate-500">
                No notifications yet.
              </div>
            )}
            {data.notifications.slice(0, 6).map((n) => (
              <button
                key={n.notification_id}
                onClick={() => n.application_id && nav(`/broker/applications/${n.application_id}`)}
                className="w-full rounded-md border border-slate-800 bg-slate-950 p-3 text-left hover:border-emerald-500/30"
                data-testid={`recent-notif-${n.notification_id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-100">{n.title}</div>
                    <div className="mt-0.5 text-xs text-slate-400 line-clamp-2">{n.message}</div>
                    <div className="mt-1 text-[11px] text-slate-500">{fmtDateTime(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
